import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import { Button } from '@/components/Button/Button';
import { useCourse } from '@/features/courses/useCourse';
import { useReaderTimeline } from '@/features/content/hooks/useReaderTimeline';
import { useComponent } from '@/features/content/useComponent';
import { isInstructionalRole, useUserRole } from '@/hooks/useUserRole';

import { ChapterPdfDocument } from './ChapterPdfDocument';
import { getPrintableChapter } from './printableChapter';
import { waitForPrintReadiness } from './printReadiness';
import styles from './ChapterPdfPrintPage.module.css';

type PrintStatus = 'loading' | 'preparing' | 'ready' | 'error';

function shouldIncludeOption(value: string | null) {
  return value !== '0';
}

function collectCurrentStylesheets() {
  const stylesheets: unknown[] = [];

  document
    .querySelectorAll<HTMLStyleElement>(
      'style:not([data-pagedjs-inserted-styles])',
    )
    .forEach((styleElement, index) => {
      stylesheets.push({
        [`inline-style-${index}`]: styleElement.textContent ?? '',
      });
    });

  document
    .querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
    .forEach((linkElement) => {
      stylesheets.push(linkElement.href);
    });

  return stylesheets;
}

export function ChapterPdfPrintPage() {
  const { contentId, courseId } = useParams();
  const [searchParams] = useSearchParams();
  const sourceRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const hasPreparedRef = useRef(false);
  const [status, setStatus] = useState<PrintStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const includeVideoUrls = shouldIncludeOption(searchParams.get('videos'));
  const includeQuestions = shouldIncludeOption(searchParams.get('questions'));
  const courseQuery = useCourse(courseId);
  const componentQuery = useComponent(
    courseId,
    contentId,
    courseQuery.data?.isInstructorView === true,
    {
      coursePrelectureQuestionSettings:
        courseQuery.data?.coursePrelectureQuestionSettings,
      courseQuestionSettings: courseQuery.data?.courseQuestionSettings,
    },
  );
  // Until the course has been read the reader counts as a student, as on the
  // chapter page, so nothing is drawn that they might not see.
  const userRole = useUserRole(courseQuery.data);
  const { timeline } = useReaderTimeline(componentQuery.data);
  const courseTitle = courseQuery.data?.title ?? 'Course';
  const chapterTitle = componentQuery.data?.title ?? 'Chapter';
  const isLoading = courseQuery.isPending || componentQuery.isPending;
  const isError = courseQuery.isError || componentQuery.isError;

  const preparePrintPreview = useCallback(async () => {
    const source = sourceRef.current;
    const preview = previewRef.current;

    if (!source || !preview) {
      return;
    }

    setStatus('preparing');
    setErrorMessage(null);
    preview.innerHTML = '';

    try {
      await waitForPrintReadiness(source);
      const { Previewer } = await import('pagedjs');
      const previewer = new Previewer();
      await previewer.preview(source, collectCurrentStylesheets(), preview);

      setStatus('ready');

      window.setTimeout(() => {
        window.print();
      }, 120);
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Unable to prepare this chapter for printing.',
      );
    }
  }, []);

  useEffect(() => {
    if (isLoading) {
      setStatus('loading');
      return;
    }

    if (isError) {
      setStatus('error');
      setErrorMessage('Unable to load this chapter for export.');
      return;
    }

    if (!componentQuery.data || hasPreparedRef.current) {
      return;
    }

    hasPreparedRef.current = true;
    void preparePrintPreview();
  }, [componentQuery.data, isError, isLoading, preparePrintPreview]);

  const backHref =
    courseId && contentId ? `/app/courses/${courseId}/${contentId}` : '/app/courses';

  if (!courseId || !contentId) {
    return (
      <main className={styles.page}>
        <section className={styles.stateCard}>
          <h1 className={styles.stateTitle}>Unable to export chapter</h1>
          <p className={styles.stateText}>
            This print view is missing a course or chapter ID.
          </p>
          <Button as={Link} to="/app/courses" variant="secondary">
            Back to courses
          </Button>
        </section>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <div className={styles.screenToolbar}>
        <p aria-live="polite" className={styles.status}>
          {status === 'loading'
            ? 'Loading chapter export...'
            : status === 'preparing'
              ? 'Preparing print-ready pages...'
              : status === 'ready'
                ? 'Print view is ready. Use Save as PDF in the print dialog.'
                : (errorMessage ?? 'Unable to prepare print view.')}
        </p>
        <div className={styles.actions}>
          <Button as={Link} to={backHref} variant="secondary">
            Back
          </Button>
          <Button
            disabled={status === 'loading' || status === 'preparing'}
            onClick={() => {
              if (status === 'ready') {
                window.print();
                return;
              }

              hasPreparedRef.current = false;
              void preparePrintPreview();
            }}
          >
            {status === 'ready' ? 'Print again' : 'Retry'}
          </Button>
        </div>
      </div>

      {componentQuery.data ? (
        <div className={styles.sourceDocument} ref={sourceRef}>
          <ChapterPdfDocument
            chapterTitle={chapterTitle}
            courseTitle={courseTitle}
            includeQuestions={includeQuestions}
            includeVideoUrls={includeVideoUrls}
            printable={getPrintableChapter(componentQuery.data, {
              isInstructionalView: isInstructionalRole(userRole),
              timeline,
            })}
          />
        </div>
      ) : null}

      <div
        aria-label="PDF page preview"
        className={styles.preview}
        ref={previewRef}
      />
    </main>
  );
}
